/*
 * SubmitRecipeLink — public submission page for logged-in FoodPulse users.
 *
 * Route: /food-pulse/submit-recipe-link
 *
 * Anyone signed in can paste a public recipe/video URL. The link goes to
 * the admin review queue (never auto-published). We honour copyright by
 * only ever storing factual metadata + linking back to the original.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Link2, ExternalLink, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";

const PLATFORM_ICON = {
  youtube: "▶", tiktok: "♪", instagram: "◈", facebook: "f", web: "◫",
};

export default function SubmitRecipeLink() {
  const nav = useNavigate();
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [authed, setAuthed] = useState(null);

  // Check auth on mount
  useEffect(() => {
    http.get("/auth/me").then((r) => setAuthed(!!r.data?.id)).catch(() => setAuthed(false));
  }, []);

  const submit = async () => {
    if (!url.trim()) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await http.post("/recipe-discovery/sources", {
        url: url.trim(), notes_for_ai: notes,
      });
      setResult(r.data.source);
      setUrl(""); setNotes("");
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setErr(String(detail));
    } finally {
      setBusy(false);
    }
  };

  if (authed === false) {
    return (
      <div className="max-w-[720px] mx-auto px-6 py-16 text-center" data-testid="submit-link-signin-prompt">
        <div className="rounded-2xl border p-8" style={{ borderColor: "var(--wofy-line)" }}>
          <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
            <Sparkles size={13} className="inline mr-1.5 -mt-0.5" /> SHARE A RECIPE LINK
          </div>
          <h1 className="font-display text-3xl mb-3">Sign in to share recipe links</h1>
          <p className="text-sm mb-5" style={{ color: "var(--wofy-text-soft)" }}>
            FoodPulse credits every creator we discover — and your submissions land in the
            editorial queue so we can review before publishing.
          </p>
          <button onClick={() => nav("/login?next=/food-pulse/submit-recipe-link")}
                  data-testid="signin-cta"
                  className="px-5 py-2.5 rounded-full text-sm font-semibold"
                  style={{ background: "var(--wofy-primary)", color: "white" }}>
            Sign in / Sign up
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-6 py-10" data-testid="submit-link-page">
      <div className="mb-6">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
          <Link2 size={13} className="inline mr-1.5 -mt-0.5" /> SUBMIT A RECIPE LINK
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight">Discovered a recipe worth sharing?</h1>
        <p className="text-sm mt-3 max-w-[64ch]" style={{ color: "var(--wofy-text-soft)" }}>
          Paste any public YouTube / TikTok / Instagram / Facebook / blog / recipe-site URL.
          We'll credit the original creator, extract only factual metadata, and add it to our editorial queue.
          <strong className="ml-1">We never re-host copyrighted content.</strong>
        </p>
      </div>

      <div className="rounded-2xl border p-6" style={{ borderColor: "#DCEAD1", background: "#F5FAF0" }}>
        <label className="block text-xs font-mono uppercase tracking-widest opacity-70 mb-1">Public URL</label>
        <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
               data-testid="public-url-input"
               placeholder="https://…"
               className="w-full border rounded-lg px-3 py-2.5 text-sm" style={{ borderColor: "#DCEAD1" }} />
        <label className="block text-xs font-mono uppercase tracking-widest opacity-70 mb-1 mt-4">Notes (optional)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  data-testid="public-notes-input"
                  placeholder="Anything special about the recipe or the creator we should know…"
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2.5 text-sm" style={{ borderColor: "#DCEAD1" }} />
        <button onClick={submit} disabled={busy || !url.trim() || !authed}
                data-testid="public-submit-btn"
                className="mt-4 px-6 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50"
                style={{ background: "var(--wofy-primary)", color: "white" }}>
          {busy ? "Analyzing…" : "Analyze & submit"}
        </button>
        {err && (
          <div className="mt-3 text-sm inline-flex items-center gap-1.5" style={{ color: "#B04A3E" }} data-testid="public-error">
            <AlertTriangle size={14} /> {err}
          </div>
        )}
      </div>

      {result && (
        <div className="mt-6 rounded-2xl border p-5 bg-white" style={{ borderColor: "#DCEAD1" }} data-testid="submit-result">
          <div className="inline-flex items-center gap-1.5 text-sm mb-3" style={{ color: "#3E5A2A" }}>
            <CheckCircle2 size={15} /> Thanks — added to the editorial queue!
          </div>
          <div className="flex items-start gap-4">
            {result.thumbnail_url ? (
              <img src={result.thumbnail_url} alt="" className="w-28 h-20 rounded object-cover"
                   onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="w-28 h-20 rounded flex items-center justify-center text-2xl"
                   style={{ background: "#F2ECDE" }}>{PLATFORM_ICON[result.platform] || "◫"}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">
                {result.platform}{result.source_type ? ` · ${result.source_type}` : ""}
              </div>
              <div className="font-display text-base line-clamp-2 mt-0.5">{result.title || "(title not yet extracted)"}</div>
              {result.creator_name && (
                <div className="text-xs opacity-70">by {result.creator_name}</div>
              )}
              {result.description && (
                <div className="text-xs mt-1 opacity-80 line-clamp-2">{result.description}</div>
              )}
              <a href={result.canonical_url || result.original_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 text-xs underline mt-2">
                <ExternalLink size={11} /> View original
              </a>
            </div>
          </div>
          <div className="text-xs mt-3 opacity-70">
            Submit another link, or <Link to="/recipes" className="underline">browse published recipes</Link>.
          </div>
        </div>
      )}

      <div className="mt-8 text-xs opacity-70">
        By submitting, you confirm the link is publicly accessible and you're not circumventing paywalls or private accounts.
        FoodPulse credits every original creator and links every recipe back to the source.
      </div>
    </div>
  );
}
